import { lazy, Suspense } from "react";
// import Feedback from "./Feedback";
import FindMyCar from "./FindMyCar";
import Offers from "./Offers";
import { IoCloseSharp } from "react-icons/io5";
import { Button } from "./ui/button";
import useMapStore from "@/store/mapStore";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { t } from "i18next";
import ArrowLeft from "@/assets/citynav-arrow-left.svg";
import ArrowRight from "@/assets/citynav-arrow-right.svg";
import WelcomeWindow from "./WelcomeWindow";
const Feedback = lazy(() => import("./Feedback.js"));

function Overlay({
  content,
}: {
  content: "findMyCar" | "offers" | "feedback" | "cityNav" | "welcome";
}) {
  const mobileMode = useMapStore((state) => state.mobileMode);
  const verticalKioskMode = useMapStore((state) => state.verticalKioskMode);
  const kioskScaling = useMapStore((state) => state.kioskScaling);
  const setOverlayContent = useMapStore((state) => state.setOverlayContent);
  const setActiveMenuItem = useMapStore((state) => state.setActiveMenuItem);
  const setNavMode = useMapStore((state) => state.setNavMode);
  const setShowCustomRoutePicker = useMapStore(
    (state) => state.setShowCustomRoutePicker,
  );
  const mallTitle = t(import.meta.env.VITE_WAYFINDING_MALL_TITLE);
  const welcomeEnabled =
    import.meta.env.VITE_WAYFINDING_ENABLE_WELCOME === "true";

  const closeHandler = () => {
    setOverlayContent(undefined);
    setActiveMenuItem("map");
  };

  const setNavModeHandler = (mode: "city-to" | "city-from") => {
    setNavMode(mode);
    setShowCustomRoutePicker(true);
    closeHandler();
  };

  if (content === "welcome" && welcomeEnabled) {
    return <WelcomeWindow closeHandler={closeHandler} />;
  }
  ``;

  if (content === "cityNav") {
    return (
      <AnimatePresence>
        <div
          className={cn(
            "fixed z-100 flex h-full w-full items-center justify-center bg-black/50",
            mobileMode && "items-end",
            "z-[102]",
            !kioskScaling && "kiosk:origin-top-right kiosk:scale-150",
          )}
          style={{
            //@ts-expect-error color
            "--custom-handle-bg": `var(--primary)`,
          }}
        >
          <motion.div
            key="overlay"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.2 }}
            className="relative max-w-xl rounded-3xl rounded-b-none bg-white p-5 pt-5 shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)] lg:rounded-b-3xl lg:p-8"
          >
            <Button
              onClick={closeHandler}
              size="icon"
              className={cn(
                "absolute top-5 z-100 h-10 w-10 rounded-xl group-data-[dir=ltr]:right-5 group-data-[dir=rtl]:left-5",
                mobileMode &&
                  "top-5 h-8 w-8 rounded-lg text-lg group-data-[dir=ltr]:right-5 group-data-[dir=rtl]:left-5",
              )}
            >
              <IoCloseSharp />
            </Button>
            <h1 className="mb-5 text-2xl font-bold lg:my-5 lg:text-center">
              {t("explore-navigate")}
            </h1>
            <p className="mb-8 text-center">
              {t("explore-paragraph-1")} {mallTitle} {t("explore-paragraph-2")}
            </p>
            <div className="flex flex-col gap-3">
              <Button
                className="w-full rounded-full"
                size={mobileMode ? "sm" : "xl"}
                onClick={() => setNavModeHandler("city-to")}
              >
                <img
                  src={ArrowRight}
                  alt={t("citynav-to")}
                  className="w-[24px] group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2"
                />
                {t("citynav-to")} {mallTitle}
              </Button>
              <Button
                className="w-full rounded-full"
                size={mobileMode ? "sm" : "xl"}
                onClick={() => setNavModeHandler("city-from")}
              >
                <img
                  src={ArrowLeft}
                  alt={t("citynav-from")}
                  className="w-[24px] group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2"
                />
                {t("citynav-from")} {mallTitle}
              </Button>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <div
        className={cn(
          verticalKioskMode &&
            "fixed z-100 flex h-full w-full items-center justify-center",
          verticalKioskMode &&
            content === "offers" &&
            "bg-black/80 backdrop-blur-sm",
          !kioskScaling && "kiosk:scale-150",
        )}
      >
        <motion.div
          key="overlay"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          transition={{ duration: 0.2 }}
          className={cn(
            "fixed z-100 h-full w-full bg-white p-10",
            mobileMode && "px-4 pb-20 pt-16",
            "z-[102]",
            content === "feedback" && "p-0",
            content === "offers" && "pt-6",
            verticalKioskMode &&
              "relative h-[50%] max-w-[90%] rounded-3xl bg-white p-5 pt-5 shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)]",
            !kioskScaling && "kiosk:h-[25%] kiosk:max-w-[64%]",
          )}
          style={{
            //@ts-expect-error color
            "--custom-handle-bg": `var(--primary)`,
          }}
        >
          <Button
            onClick={closeHandler}
            size="icon"
            className={cn(
              "absolute top-10 z-100 h-12 w-12 rounded-xl group-data-[dir=ltr]:right-10 group-data-[dir=rtl]:left-10",
              mobileMode &&
                "top-5 h-8 w-8 rounded-lg text-lg group-data-[dir=ltr]:right-5 group-data-[dir=rtl]:left-5",
            )}
          >
            <IoCloseSharp />
          </Button>
          {content === "findMyCar" && <FindMyCar />}
          {content === "offers" && <Offers />}
          {content === "feedback" && (
            <Suspense>
              <Feedback />
            </Suspense>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

export default Overlay;
